import "../category.scss"; //import styles from "./Category.module.scss" if you want to use module

import { PageProps, graphql } from "gatsby";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import CategoryFilters from "./CategoryFilters";
import { usePageLocalization } from "@sbi-international/boutik.gatsby.plugin.multilocale";
import { SEOArguments } from "../../classes/seoArguments";
import { LocalType } from "../../models/localType";
import SEO from "../../components/Seo";
import { PartsListing } from "../../components/productListing";
import MainLayout from "../../components/layout/MainLayout";

const applianceTypes = [
    { fr_CA: "Tous", en_CA: "All", en_US: "All" },
    { fr_CA: "Poêles à bois", en_CA: "Stoves", en_US: "Stoves" },
    { fr_CA: "Encastrables à bois", en_CA: "Inserts", en_US: "Inserts" },
];

export const query = graphql`
    query PartsTemplate {
        parts: allProductsJson(
            filter: { category: { regex: "^/Century/Heaters/" } }
            sort: { title: { en_CA: ASC } }
        ) {
            nodes {
                code
                id
                product_name {
                    en_CA
                    en_US
                    fr_CA
                }
                title {
                    en_CA
                    en_US
                    fr_CA
                }
                documents {
                    en_CA
                    en_US
                    fr_CA
                }
                parts {
                    code
                }
                slug {
                    en_CA
                    en_US
                    fr_CA
                }
                category
                attributes {
                    custom_made {
                        group
                        name {
                            en_US
                            en_CA
                            fr_CA
                        }
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    serial_number_from {
                        name {
                            en_CA
                            en_US
                            fr_CA
                        }
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                        group
                    }
                    serial_number_to {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        }
        metadata: allProductsJson(filter: { code: { eq: "century_pieces" } }) {
            nodes {
                code
                attributes {
                    meta_title {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    meta_description {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        }
    }
`;

const getCategoryName = (categoryPath: any, currentLocale: any, applianceTypes: any) => {
    if (categoryPath.includes("stove")) return applianceTypes[1][currentLocale].toLowerCase();

    if (categoryPath.includes("insert")) return applianceTypes[2][currentLocale].toLowerCase();
};

export default function Parts({ data }: PageProps<Queries.PartsTemplateQuery>) {
    const products = data.parts.nodes;
    const [search, setSearch] = useState("");
    const { currentLocale } = usePageLocalization();
    const [type, setType] = useState(applianceTypes[0][currentLocale]);
    const [category, setCategory] = useState("");
    const [serialNumber, setSerialNumber] = useState("");
    const { t } = useTranslation("templatePages");

    const seoArguments = new SEOArguments(data.metadata, currentLocale as keyof LocalType);

    const filteredProducts = useMemo(
        () =>
            products.filter((product: any) =>
                serialNumber === ""
                    ? (product.title[currentLocale].toLowerCase().includes(search.toLowerCase()) ||
                          product.code.toLowerCase().includes(search.toLowerCase())) &&
                      (type.toLowerCase() === applianceTypes[0][currentLocale].toLowerCase() ||
                          type.toLowerCase() ===
                              getCategoryName(
                                  product.category.substring(16).toLowerCase(),
                                  currentLocale,
                                  applianceTypes
                              ))
                    : (product.title[currentLocale].toLowerCase().includes(search.toLowerCase()) ||
                          product.code.toLowerCase().includes(search.toLowerCase())) &&
                      (type.toLowerCase() === applianceTypes[0][currentLocale].toLowerCase() ||
                          type.toLowerCase() ===
                              getCategoryName(
                                  product.category.substring(16).toLowerCase(),
                                  currentLocale,
                                  applianceTypes
                              )) &&
                      parseInt(serialNumber) >
                          product.attributes?.serial_number_from?.values[0][currentLocale] &&
                      (product.attributes?.serial_number_to === null ||
                          parseInt(serialNumber) <
                              product.attributes?.serial_number_to?.values[0][currentLocale])
            ),
        [products, search, type, serialNumber, currentLocale]
    );

    return (
        <div className="category">
            <MainLayout>
                <SEO title={seoArguments.title} description={seoArguments.description} />

                <div className="container">
                    <div className="row categoryRow">
                        <aside id="secondary">
                            <CategoryFilters
                                search={search}
                                type={type}
                                serialNumber={serialNumber}
                                setSearch={setSearch}
                                setType={setType}
                                setSerialNumber={setSerialNumber}
                                applianceTypes={applianceTypes}
                            />
                        </aside>

                        <div id="primary">
                            <header>
                                <div className="container">
                                    <h1>{t("parts-title")}</h1>
                                </div>
                            </header>                            
                            <PartsListing products={filteredProducts} columns={[]} />
                            
                        </div>
                    </div>
                </div>
            </MainLayout>
        </div>
    );
}
